import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { CommonFormValidationMessagesComponent } from '../../forms';
import {
  LoaderGlobeAnimationDetailComponent,
  LoadingObject,
  LoadingService,
} from '../../loaders';
import {
  ProfileUserPatchResource,
  ProfileUserService,
  UpdateUserProfileRequestBody,
} from '../../profile-user/profile-user.service';
import { startWith } from 'rxjs';
import { FormCachingService } from '../../utilities';

const FORM_CACHING_KEY = 'professional_bio';

@Component({
  selector: 'multisite-professional-bio',
  templateUrl: './professional-bio.component.html',
  styleUrls: ['./professional-bio.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    AlertModule,
    LoaderGlobeAnimationDetailComponent,
    CommonFormValidationMessagesComponent,
  ],
  providers: [
    provideTranslocoScope('profile'),
  ],
})
export class ProfessionalBioComponent {
  private readonly profileUserService = inject(ProfileUserService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formCachingService = inject(FormCachingService);

  readonly userProfile = signal<ProfileUserPatchResource | null | undefined>(undefined);
  readonly submitAttempted = signal(false);
  readonly error = signal<string | null>(null);

  // Local loading object is reserved for component-level async work when needed.
  loadingObject = signal<LoadingObject>({});

  readonly bioForm = new FormGroup({
    job_title: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.minLength(2), Validators.maxLength(255)],
    }),
    summary: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.minLength(20), Validators.maxLength(90)],
    }),
    bio: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.minLength(100), Validators.maxLength(750)],
    }),
  });

  private readonly formValue = toSignal(
    this.bioForm.valueChanges.pipe(startWith(this.bioForm.getRawValue())),
    { initialValue: this.bioForm.getRawValue() },
  );

  readonly initialLoading = computed(() => this.userProfile() === undefined);

  readonly isAnythingLoading = computed<boolean>(() => {
    const requestInFlight = this.profileUserService.loadingObjectSignal()['userProfile'] === true;
    return requestInFlight || this.loadingService.isAnythingLoading(this.loadingObject());
  });

  readonly formDisabled = computed(() => this.initialLoading() || this.isAnythingLoading());

  readonly subtitle = computed(() => {
    const profile = this.userProfile();
    if (!profile) {
      return '';
    }

    const jobTitle = this.coerceToTrimmedString(profile.job_title);
    const summary = this.coerceToTrimmedString(profile.summary);
    return jobTitle || summary;
  });

  readonly hasUnsavedChanges = computed(() => {
    const profile = this.userProfile();
    if (profile === undefined) {
      return false;
    }

    const currentFormValue = this.formValue();
    const currentComparable = {
      job_title: this.coerceToTrimmedString(currentFormValue.job_title),
      summary: this.coerceToTrimmedString(currentFormValue.summary),
      bio: this.coerceToTrimmedString(currentFormValue.bio),
    };

    const storedComparable = this.getComparableProfileValues(profile);
    return (
      currentComparable.job_title !== storedComparable.job_title
      || currentComparable.summary !== storedComparable.summary
      || currentComparable.bio !== storedComparable.bio
    );
  });  

  constructor() {
    effect(() => {
      if (this.formDisabled()) {
        this.bioForm.disable({ emitEvent: false });
      } else {
        this.bioForm.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.profileUserService.userProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        const previousProfile = this.userProfile();
        // Read first: filling the form from the profile below forgets the draft
        const draft = previousProfile === undefined ? this.formCachingService.getDataItem(FORM_CACHING_KEY) : null;
        this.userProfile.set(profile);

        // Pre-populate and re-sync form values from the service store subscription.
        if (profile && (!this.bioForm.dirty || previousProfile === undefined || previousProfile === null)) {
          this.patchFormFromProfile(profile);
        }

        // After a reload, unsaved changes come back over the saved profile
        if (draft) {
          this.bioForm.patchValue(draft);
          this.bioForm.markAsDirty();
        }
      });

    // A draft is kept only while the form differs from the saved profile
    this.bioForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const profile = this.userProfile();
        if (profile === undefined) {
          return;
        }
        if (this.differsFromProfile(profile)) {
          this.formCachingService.cacheFormData(this.bioForm.getRawValue(), FORM_CACHING_KEY);
        } else {
          this.formCachingService.clearDataItem(FORM_CACHING_KEY);
        }
      });

    this.requestInitialProfileIfNeeded();
  }

  private requestInitialProfileIfNeeded(): void {
    const isUserProfileLoading = this.profileUserService.loadingObjectSignal()['userProfile'] === true;
    const hasUserProfile = this.profileUserService.userProfileState !== undefined;
    if (!isUserProfileLoading && !hasUserProfile) {
      this.profileUserService
        .getUserProfile()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (errorResponse) => {
            this.error.set(
              typeof errorResponse === 'string'
                ? errorResponse
                : errorResponse?.message ?? 'multisite.something_went_wrong',
            );
          },
        });
    }
  }

  private patchFormFromProfile(profile: ProfileUserPatchResource): void {
    const comparableProfileValues = this.getComparableProfileValues(profile);
    this.bioForm.reset({
      job_title: comparableProfileValues.job_title,
      summary: comparableProfileValues.summary,
      bio: comparableProfileValues.bio,
    });
    this.bioForm.markAsPristine();
    this.bioForm.markAsUntouched();
  }

  private differsFromProfile(profile: ProfileUserPatchResource | null): boolean {
    const current = this.bioForm.getRawValue();
    const stored = this.getComparableProfileValues(profile);
    return (
      this.coerceToTrimmedString(current.job_title) !== stored.job_title
      || this.coerceToTrimmedString(current.summary) !== stored.summary
      || this.coerceToTrimmedString(current.bio) !== stored.bio
    );
  }

  private getComparableProfileValues(profile: ProfileUserPatchResource | null) {
    return {
      job_title: this.coerceToTrimmedString(profile?.job_title),
      summary: this.coerceToTrimmedString(profile?.summary),
      bio: this.coerceToTrimmedString(profile?.bio),
    };
  }

  private coerceToTrimmedString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private normalizeFieldValue(value: string): string | null {
    const trimmedValue = value.trim();
    return trimmedValue.length ? trimmedValue : null;
  }

  private buildRequestBody(): UpdateUserProfileRequestBody {
    return {
      job_title: this.normalizeFieldValue(this.bioForm.controls.job_title.value),
      summary: this.normalizeFieldValue(this.bioForm.controls.summary.value),
      bio: this.normalizeFieldValue(this.bioForm.controls.bio.value),
    };
  }

  private applyFieldErrors(errorResponse: any): boolean {
    const validationErrors = errorResponse?.errors;

    if (!validationErrors) {
      return false;
    }

    const entries = Object.entries(validationErrors);
    if (!entries.length) {
      return false;
    }

    entries.forEach(([fieldName, messages]) => {
      const control = this.bioForm.get(fieldName);
      if (!control) {
        return;
      }

      const messageList = Array.isArray(messages) ? messages : [messages];
      const backendMessage = String(messageList[0] ?? '').toLowerCase();
      const nextErrors: Record<string, boolean> = { ...(control.errors ?? {}) };

      if (backendMessage.includes('at least') || backendMessage.includes('minimum')) {
        nextErrors.minlength = true;
      } else if (backendMessage.includes('greater than') || backendMessage.includes('maximum')) {
        nextErrors.maxlength = true;
      } else if (backendMessage.includes('required')) {
        nextErrors.required = true;
      } else {
        nextErrors.backend = true;
      }

      control.setErrors(nextErrors);
      control.markAsTouched();
    });

    return true;
  }

  saveProfile(): void {
    this.submitAttempted.set(true);

    if (this.isAnythingLoading()) {
      return;
    }

    if (this.bioForm.invalid) {
      this.bioForm.markAllAsTouched();
      return;
    }

    this.error.set(null);

    this.profileUserService
      .updateUserProfile(this.buildRequestBody())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.formCachingService.clearDataItem(FORM_CACHING_KEY);
          const latestProfile = this.userProfile();
          if (latestProfile) {
            this.patchFormFromProfile(latestProfile);
          }
          this.submitAttempted.set(false);
        },
        error: (errorResponse) => {
          const matchedFieldErrors = this.applyFieldErrors(errorResponse);
          if (!matchedFieldErrors) {
            this.error.set(
              typeof errorResponse === 'string'
                ? errorResponse
                : errorResponse?.message ?? 'multisite.something_went_wrong',
            );
          }
        },
      });
  }
}
